/**
 * useStaffLog — lightweight hook for logging staff activity.
 *
 * Import and call `log(...)` anywhere a staff member performs an action.
 * For admins this is a no-op (returns immediately without an API call).
 *
 * Usage:
 *   const { log } = useStaffLog();
 *
 *   // After creating a bill:
 *   await log('created_invoice', `Created invoice #${bill.id} for ${business.name}`, 'Bill', bill.id);
 *
 *   // After updating a sales order:
 *   await log('updated_order', `Updated order #${order.orderNumber}`, 'SalesOrder', order.id);
 */

import { useCallback } from 'react';
import API from './api';
import { useAuth } from '../context/AuthContext';

export const useStaffLog = () => {
  const { user } = useAuth();

  const log = useCallback(async (
    action,
    description = null,
    entity      = null,
    entityId    = null,
    meta        = null,
  ) => {
    // No-op for admins and non-staff
    if (!user || user.role !== 'staff') return;

    try {
      await API.post('/admin/staff/log', { action, description, entity, entityId, meta });
    } catch {
      // Never let logging break the user's workflow
    }
  }, [user]);

  return { log };
};
